// Texts of the Customer Management page and its dialogs.
const customers = {
  title: 'Customer Management',
  subtitle: 'Search and filter customers; manage their agreements, addresses and contacts.',
  listLabel: 'Customer list',
  yes: 'Yes',
  no: 'No',
  domestic: 'Domestic',
  abroad: 'International',
  paymentDays_one: '{{count}} day',
  paymentDays_other: '{{count}} days',
  status: {
    active: 'Active',
    inactive: 'Inactive',
    all: 'All',
    unknown: 'Not specified',
  },
  stats: {
    total: 'Total customers',
    totalShort: 'Total',
    active: 'Active customers',
    activeShort: 'Active',
    inactive: 'Inactive customers',
    inactiveShort: 'Inactive',
    share: 'Share of all customers: {{percent}}%',
  },
  actions: {
    add: 'Add Customer',
    download: 'Download Table',
    downloadHint: 'Download every customer as Excel, with the columns shown in the table',
  },
  search: {
    placeholder: 'Search by name, info or tax number…',
    placeholderShort: 'Search name, info or tax no…',
    label: 'Search customers (at least 3 characters)',
  },
  columns: {
    title: 'Name',
    info: 'Info',
    status: 'Status',
    agreementDate: 'Master Agreement Date',
    agreementNumber: 'Master Agreement No.',
    currency: 'Currency',
    serviceNumber: 'Customer Service No.',
    deliveryDate: 'Delivery Date',
    taxNumber: 'Tax Number',
    domestic: 'Domestic?',
    paymentPeriod: 'Payment Period',
  },
  options: {
    monthly: 'Monthly',
    quarterly: 'Every 3 months',
    halfYearly: 'Every 6 months',
    yearly: 'Yearly',
  },
  table: {
    empty: 'No customers match the filters',
  },
  pagination: {
    rangeOf: 'of {{total}} customers',
    ofTotal: 'out of {{total}} in total',
  },
  panel: {
    label: 'Customer details',
    heading: 'Customer',
    card: 'Customer Card',
    edit: 'Edit',
    addresses: 'Addresses',
    personnel: 'Contacts',
    deactivate: 'Set Inactive',
    activate: 'Set Active',
    statusHint: "Change the customer's status",
  },
  toast: {
    loadError: 'Customers could not be loaded',
    excelPreparing: 'Preparing the Excel file...',
    excelSuccess: 'Excel file downloaded',
    excelError: 'The Excel file could not be downloaded',
  },
  common: {
    save: 'Save',
    update: 'Update',
    saving: 'Saving…',
    cancel: 'Cancel',
    back: 'Back to list',
    edit: 'Edit',
    delete: 'Delete',
    deleting: 'Deleting…',
    keep: 'Keep',
    loading: 'Loading…',
  },
  card: {
    title: 'Customer Card',
    edit: 'Edit',
    summary: 'Summary',
    totalProjects: 'Total projects',
    activeProjects: 'Active projects',
    people: 'Contacts',
    tariffs: 'Tariffs',
    company: 'Company details',
    name: 'Customer name',
    agreement: 'Agreement details',
    addresses: 'Addresses ({{count}})',
    defaultAddress: 'Default address',
    address: 'Address',
    cityDistrict: 'City / District',
    fullAddress: 'Full address',
    noAddress: 'No addresses on record.',
    personnel: 'Contacts ({{count}})',
    noPersonnel: 'No contacts on record.',
    personFallback: 'Contact',
    morePersonnel: '+{{count}} more',
    manage: 'Manage',
    loadError: "The customer's related records could not be loaded",
  },
  form: {
    addTitle: 'Add Customer',
    editTitle: 'Edit Customer',
    addSubtitle: 'Enter the company and agreement details',
    company: 'Company details',
    agreement: 'Agreement and payment',
    fields: {
      name: 'Customer name',
      nameHint: 'Saved in capital letters',
      info: 'Info',
      agreementNumber: 'Master agreement number',
      agreementDate: 'Master agreement date',
      status: 'Status',
      currency: 'Currency',
      serviceNumber: 'Customer service number',
      endDate: 'Contract end date',
      endDateHint: 'Shown in the "Delivery Date" column',
      taxNumber: 'Tax number',
      domestic: 'Domestic?',
      paymentPeriod: 'Payment period',
      paymentPeriodHint: 'Number of days',
    },
    errors: {
      nameRequired: 'Customer name is required',
      paymentInvalid: 'Payment period must be a valid number',
    },
    submitAdd: 'Save',
    submitEdit: 'Update',
    shortcut: 'Ctrl + Enter to save',
    toast: {
      fixErrors: 'Please correct the form errors',
      added: 'Customer added',
      addError: 'The customer could not be added: {{message}}',
      updated: 'Customer updated',
      updateError: 'The customer could not be updated: {{message}}',
      notFound: 'The customer to update was not found',
    },
  },
  statusChange: {
    title: 'Change Status',
    toInactive: "Change this customer's status from active to inactive?",
    toActive: "Change this customer's status from inactive to active?",
    confirm: 'Yes, change it',
    cancel: 'No',
    success: 'Customer status updated',
    error: 'The customer status could not be updated',
  },
  addresses: {
    title: 'Addresses',
    add: 'Add Address',
    addTitle: 'Add Address',
    editTitle: 'Edit Address',
    empty: 'No addresses yet',
    emptyHint: "Add the customer's office, plant or delivery addresses here.",
    count_one: '{{count}} address',
    count_other: '{{count}} addresses',
    default: 'Default',
    makeDefault: 'Default address',
    makeDefaultHint: 'Other addresses lose their default mark',
    deleteConfirm: 'Delete this address?',
    fields: {
      city: 'City',
      district: 'District',
      address: 'Address',
      addressPlaceholder: 'Full address',
      title: 'Title',
      titlePlaceholder: 'e.g. Head office, Plant',
      postalCode: 'Postal code',
      notes: 'Notes',
      notesPlaceholder: 'Additional notes',
    },
    required: 'Title and address are required',
    toast: {
      loadError: 'Addresses could not be loaded',
      added: 'Address added',
      addError: 'The address could not be added',
      updated: 'Address updated',
      updateError: 'The address could not be updated',
      deleted: 'Address deleted',
      deleteError: 'The address could not be deleted',
    },
  },
  personnel: {
    title: 'Contacts',
    add: 'Add Contact',
    addTitle: 'Add Contact',
    editTitle: 'Edit Contact',
    empty: 'No contacts yet',
    emptyHint: "Add the customer's contact people here.",
    count_one: '{{count}} contact',
    count_other: '{{count}} contacts',
    deleteConfirm: 'Delete this contact?',
    fields: {
      firstName: 'First name',
      lastName: 'Last name',
      position: 'Position',
      department: 'Department',
      email: 'E-mail',
      emailPlaceholder: 'E-mail address',
      phone: 'Phone',
      phonePlaceholder: 'Phone number',
      notes: 'Notes',
      notesPlaceholder: 'Additional notes',
    },
    required: 'First and last name are required',
    emailInvalid: 'Enter a valid e-mail address',
    toast: {
      loadError: 'Contacts could not be loaded',
      added: 'Contact added',
      addError: 'The contact could not be added',
      updated: 'Contact updated',
      updateError: 'The contact could not be updated',
      deleted: 'Contact deleted',
      deleteError: 'The contact could not be deleted',
    },
  },
};

export default customers;
